import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, FileText, Send, PlayCircle, StopCircle, AlertTriangle, FileCheck, RefreshCw, Edit, Plus, Ban, Paperclip, Trash2 } from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Why a location is missing, for approvals recorded with approver_location.
const LOCATION_REASONS = {
  denied: 'permission denied or blocked',
  unavailable: 'position unavailable',
  timeout: 'timed out',
  unsupported: 'not supported on this device/connection',
  not_provided: 'not provided',
  invalid: 'invalid data received',
  unknown: 'unknown',
};

// Approver device location at approval. This is what the browser reported —
// evidence for review, not proof of physical presence — so the wording says so.
const ApproverLocationNote = ({ location }) => {
  if (!location) return null;
  const hasCoords = location.status === 'captured'
    && Number.isFinite(location.latitude) && Number.isFinite(location.longitude);
  if (!hasCoords) {
    return (
      <p className="text-xs text-gray-500 mt-1">
        Approver device location at approval: Location not captured — {LOCATION_REASONS[location.status] || LOCATION_REASONS.unknown}
      </p>
    );
  }
  const accuracy = Number.isFinite(location.accuracy_m) ? ` (±${Math.round(location.accuracy_m)}m)` : '';
  return (
    <p className="text-xs text-gray-500 mt-1">
      Approver device location at approval:{' '}
      <a
        href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        Location captured{accuracy}
      </a>
      {location.low_accuracy && <span className="ml-1 text-amber-700">· low accuracy</span>}
    </p>
  );
};

const PTWTimeline = ({ permit }) => {
  const [auditHistory, setAuditHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch audit history from API
  useEffect(() => {
    const fetchAuditHistory = async () => {
      if (!permit?.id) return;

      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_BASE}/api/v1/audit/permit/${permit.id}/history`,
          {
            params: { limit: 100 },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setAuditHistory(response.data.entries || []);
      } catch (err) {
        console.error('Failed to fetch audit history:', err);
        // Fall back to permit-based events if API fails
        setAuditHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditHistory();
  }, [permit?.id]);

  // Get icon and color based on action type
  const getActionStyle = (action) => {
    switch (action) {
      case 'create':
        return { icon: <Plus className="w-5 h-5" />, color: 'bg-gray-500' };
      case 'update':
        return { icon: <Edit className="w-5 h-5" />, color: 'bg-blue-500' };
      case 'status_change':
        return { icon: <Send className="w-5 h-5" />, color: 'bg-blue-600' };
      case 'submit':
        return { icon: <Send className="w-5 h-5" />, color: 'bg-blue-500' };
      case 'approve':
        return { icon: <CheckCircle className="w-5 h-5" />, color: 'bg-green-500' };
      case 'reject':
        return { icon: <XCircle className="w-5 h-5" />, color: 'bg-red-500' };
      case 'issue':
        return { icon: <PlayCircle className="w-5 h-5" />, color: 'bg-green-600' };
      case 'suspend':
        return { icon: <StopCircle className="w-5 h-5" />, color: 'bg-red-600' };
      case 'reissue':
        return { icon: <RefreshCw className="w-5 h-5" />, color: 'bg-green-600' };
      case 'cancel':
        return { icon: <Ban className="w-5 h-5" />, color: 'bg-red-700' };
      case 'close':
        return { icon: <FileCheck className="w-5 h-5" />, color: 'bg-purple-600' };
      case 'verify':
        return { icon: <CheckCircle className="w-5 h-5" />, color: 'bg-green-700' };
      case 'attach_files':
        return { icon: <Paperclip className="w-5 h-5" />, color: 'bg-indigo-500' };
      case 'detach_file':
        return { icon: <Trash2 className="w-5 h-5" />, color: 'bg-orange-500' };
      default:
        return { icon: <FileText className="w-5 h-5" />, color: 'bg-gray-500' };
    }
  };

  // Convert audit history entries to timeline events
  const getAuditEvents = () => {
    return auditHistory.map(entry => {
      const style = getActionStyle(entry.action);
      let title = entry.action.charAt(0).toUpperCase() + entry.action.slice(1).replace('_', ' ');
      let description = '';
      let location = null;

      // Customize title and description based on action
      if (entry.action === 'create') {
        title = 'Permit Created';
        description = `Created as ${permit?.permit_type || 'General'} permit`;
      } else if (entry.action === 'update') {
        const changedFields = entry.details?.changed_fields || [];
        description = changedFields.length > 0
          ? `Updated: ${changedFields.join(', ')}`
          : 'Permit details updated';
      } else if (entry.action === 'approve') {
        const details = entry.details || {};
        const approverName = details.approver_name || entry.actor?.name || entry.actor?.email || 'Unknown';
        const approverRole = details.approver_role || entry.actor?.role || '';
        const levelLabel = details.level_label || '';
        title = `Approved by ${approverName} (${approverRole})`;
        description = levelLabel + (details.comments ? ` — ${details.comments}` : '');
        location = details.approver_location || null;
      } else if (entry.action === 'reject') {
        const details = entry.details || {};
        const approverName = details.approver_name || entry.actor?.name || entry.actor?.email || 'Unknown';
        const approverRole = details.approver_role || entry.actor?.role || '';
        const levelLabel = details.level_label || '';
        title = `Rejected by ${approverName} (${approverRole})`;
        description = levelLabel + (details.comments ? ` — ${details.comments}` : '');
      } else if (entry.action === 'status_change') {
        const statusChange = entry.details?.status_change;
        if (statusChange) {
          title = `Status: ${statusChange.new || statusChange.new_status}`;
          description = statusChange.reason || `Changed from ${statusChange.old || statusChange.old_status}`;
        }
      } else if (entry.action === 'attach_files') {
        const filenames = entry.details?.filenames || entry.changes?.attachments?.new_value || [];
        const count = filenames.length;
        title = `${count} file${count !== 1 ? 's' : ''} attached`;
        description = filenames.join(', ');
      } else if (entry.action === 'detach_file') {
        const filename = entry.details?.filename || entry.changes?.attachments?.old_value || 'Unknown file';
        title = 'File removed';
        description = filename;
      }

      return {
        title,
        description,
        timestamp: entry.timestamp,
        icon: style.icon,
        color: style.color,
        user: entry.actor?.email || entry.actor?.name || 'System',
        location
      };
    });
  };

  // Get events from permit object (fallback/supplement)
  const getPermitEvents = () => {
    const events = [];

    // Only add permit-based events if no audit history is available
    // or to supplement with events not in audit log

    // Created event (if not in audit history)
    if (permit.created_at && auditHistory.length === 0) {
      events.push({
        title: 'Permit Created',
        description: `Created as ${permit.permit_type} permit`,
        timestamp: permit.created_at,
        icon: <FileText className="w-5 h-5" />,
        color: 'bg-gray-500',
        user: permit.applicant_name || 'System'
      });
    }

    // Approvals from permit.approvals array (supplement audit history)
    if (permit.approvals && permit.approvals.length > 0 && auditHistory.length === 0) {
      permit.approvals.forEach((approval) => {
        if (approval.approval_status === 'Approved' && approval.approval_date) {
          events.push({
            title: `Approved by ${approval.approver_role}`,
            description: approval.comments || 'Permit approved',
            timestamp: approval.approval_date,
            icon: <CheckCircle className="w-5 h-5" />,
            color: 'bg-green-500',
            user: approval.approver_name,
            location: approval.approver_location || null
          });
        } else if (approval.approval_status === 'Rejected' && approval.approval_date) {
          events.push({
            title: `Rejected by ${approval.approver_role}`,
            description: approval.comments || 'Permit rejected',
            timestamp: approval.approval_date,
            icon: <XCircle className="w-5 h-5" />,
            color: 'bg-red-500',
            user: approval.approver_name
          });
        }
      });
    }

    // Issued/Activated
    if (permit.permit_issued_date && auditHistory.length === 0) {
      events.push({
        title: 'Permit Issued & Activated',
        description: 'Permit is now active for work',
        timestamp: permit.permit_issued_date,
        icon: <PlayCircle className="w-5 h-5" />,
        color: 'bg-green-600',
        user: permit.permit_issued_by_name || 'System'
      });
    }

    // Extension approved
    if (permit.extension_approved_date && auditHistory.length === 0) {
      events.push({
        title: 'Extension Approved',
        description: `Extended until ${formatDateTime(permit.extension_end_date)}`,
        timestamp: permit.extension_approved_date,
        icon: <CheckCircle className="w-5 h-5" />,
        color: 'bg-orange-600',
        user: permit.extension_approved_by || 'System'
      });
    }

    // Suspended
    if (permit.is_suspended && permit.suspended_date && auditHistory.length === 0) {
      events.push({
        title: 'Permit Suspended',
        description: permit.suspended_reason || 'Emergency suspension',
        timestamp: permit.suspended_date,
        icon: <StopCircle className="w-5 h-5" />,
        color: 'bg-red-600',
        user: 'HSE Manager'
      });
    }

    // Closed
    if (permit.work_completed_date && auditHistory.length === 0) {
      events.push({
        title: 'Permit Closed',
        description: permit.closure_remarks || 'Work completed',
        timestamp: permit.work_completed_date,
        icon: <FileCheck className="w-5 h-5" />,
        color: 'bg-purple-600',
        user: permit.closed_by_name || 'System'
      });
    }

    // HSE Verified
    if (permit.hse_verified && permit.hse_verified_date && auditHistory.length === 0) {
      events.push({
        title: 'HSE Verification Complete',
        description: permit.hse_verification_remarks || 'Final verification completed',
        timestamp: permit.hse_verified_date,
        icon: <CheckCircle className="w-5 h-5" />,
        color: 'bg-green-700',
        user: permit.hse_verified_by_name || 'HSE Manager'
      });
    }

    // Expired
    if (permit.status === 'Expired' && auditHistory.length === 0) {
      events.push({
        title: 'Permit Expired',
        description: 'Permit validity period ended',
        timestamp: permit.work_end_date,
        icon: <AlertTriangle className="w-5 h-5" />,
        color: 'bg-gray-600',
        user: 'System'
      });
    }

    return events;
  };

  // Combine audit events with permit events
  const getAllEvents = () => {
    const auditEvents = getAuditEvents();
    const permitEvents = getPermitEvents();

    // Combine and sort by timestamp (newest first)
    const allEvents = [...auditEvents, ...permitEvents];
    return allEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const events = getAllEvents();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Status Timeline & Audit Trail
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading timeline...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Status Timeline & Audit Trail
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No timeline events available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Status Timeline & Audit Trail
          <span className="text-sm font-normal text-gray-500">
            ({events.length} events)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Scrollable container with max height */}
        <div className="max-h-96 overflow-y-auto pr-2">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

            {/* Timeline events */}
            <div className="space-y-6">
              {events.map((event, index) => (
                <div key={index} className="relative flex gap-4 items-start">
                  {/* Icon */}
                  <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full ${event.color} text-white shadow-lg flex-shrink-0`}>
                    {event.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">{event.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                        <ApproverLocationNote location={event.location} />
                        <p className="text-xs text-gray-500 mt-2">
                          <span className="font-semibold">{event.user}</span>
                          {' • '}
                          {formatDateTime(event.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {events.length > 3 && (
          <p className="text-xs text-gray-400 text-center mt-2">Scroll to see all {events.length} events</p>
        )}
      </CardContent>
    </Card>
  );
};

export default PTWTimeline;
